import React, { useState } from 'react';
import { InfoIcon } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { EmptyState } from '../../components/ui/EmptyState';
import { FeatureGate } from '../../components/platform/FeatureStatusBadge';
import { ConfidenceLine, FreshnessLine, IconTile, PowerStatusPill, SOURCE_ICON, powerIcons } from '../../components/power/PowerPrimitives';
import { usePower } from '../../contexts/PowerContext';
import { useTerminology } from '../../hooks/useTerminology';
import { managedProperties } from '../../data/property';
import { EVIDENCE_LABEL, POWER_SOURCE_LABEL, POWER_STATE_LABEL, PROPERTY_POWER_LOCATION, RELIABILITY_MINIMUM, powerLocationById } from '../../data/power';
import type { PowerSourceKind, PowerState } from '../../types/power';
const STATES: PowerState[] = ['on', 'off', 'unstable', 'backup'];
const SOURCES: PowerSourceKind[] = ['grid', 'generator', 'solar', 'inverter', 'other'];

/**
 * The property provider's half of Power.
 *
 * A manager's update carries more weight than one passing student — but it is
 * still evidence, not a decree: it is filed as `property_provider` evidence and
 * can be contradicted by residents. That is stated on the page, because a
 * landlord should know their update does not overwrite what tenants report.
 */
export function PropertyPower() {
  const {
    getLocationStatus,
    getReliability,
    reports,
    submitOperatorReport,
    offline
  } = usePower();
  const {
    term
  } = useTerminology();
  const [editing, setEditing] = useState<string | null>(null);
  const properties = term('property', 'plural').toLowerCase();
  const one = term('property', 'singular').toLowerCase();
  return <div className="space-y-6" data-id="element-9586" data-mp="3tzzs0sxjx6uy">
      <PageHeader eyebrow="Campuzon Power" title="Power at your {property:plural}" description="Confirm what residents are already reporting, or correct it. Your update is recorded as a property manager confirmation." data-id="element-9587" data-mp="2rve2zrc4pn6" />

      <Panel title="What your update does" description="Weight, not authority." bodyClassName="p-4" data-id="element-9588" data-mp="1tx8ednlmjnu2">
        <ul className="space-y-2 text-body-sm text-ink-muted" data-id="element-9589" data-mp="28mmnculag38x">
          <li className="flex gap-2" data-id="element-9590" data-mp="23tbfcq4qpgff">
            <InfoIcon className="mt-0.5 h-4 w-4 shrink-0 text-ink-subtle" aria-hidden data-id="element-9591" data-mp="23sa3ijfwgpwn" />
            Your confirmation is weighted above a single student report, so it moves the
            status quickly — but residents reporting otherwise can still contradict it, and
            the disagreement is shown rather than hidden.
          </li>
          <li className="flex gap-2" data-id="element-9592" data-mp="2mlmatyx2ec3i">
            <InfoIcon className="mt-0.5 h-4 w-4 shrink-0 text-ink-subtle" aria-hidden data-id="element-9593" data-mp="2mihyufdcyc2a" />
            You see the status of your own {properties} and the reports attached to them.
            You never see who filed a report, or where any resident was.
          </li>
          <li className="flex gap-2" data-id="element-9594" data-mp="33uybnnx1ngos">
            <InfoIcon className="mt-0.5 h-4 w-4 shrink-0 text-ink-subtle" aria-hidden data-id="element-9595" data-mp="33vzwul7rxjk8" />
            Power records are separate from occupancy, rent and maintenance. Confirming
            power here changes nothing about a tenancy.
          </li>
        </ul>
      </Panel>

      <div className="grid gap-5 lg:grid-cols-2" data-id="element-9596" data-mp="2z1vobxiv13h1">
        {managedProperties.map(property => {
        const locationId = PROPERTY_POWER_LOCATION[property.id];
        const location = locationId ? powerLocationById(locationId) : undefined;
        if (!location) {
          return <Panel key={property.id} title={property.name} bodyClassName="p-4" data-id="element-9597" data-mp="2b6spqxu97lnd">
                <EmptyState title="Not mapped to a Power location yet" description="Campuzon has no power location for this address, so residents cannot report it and no status can be shown. Ask Campuzon operations to add it — we will not attach its reports to a nearby building." data-id="element-9598" data-mp="2g5dostxrrza8" />
              </Panel>;
        }
        const status = getLocationStatus(location.id);
        const reliability = getReliability(location.id);
        const attached = reports.filter(item => item.locationId === location.id);
        return <section key={property.id} aria-labelledby={`power-${property.id}`} className="rounded-card border border-line bg-surface p-5 shadow-panel" data-id="element-9599" data-mp="1wf9t10ql3q9e">
              <div className="flex items-start justify-between gap-3" data-id="element-9600" data-mp="1mpq9dsv1rsdh">
                <div className="flex min-w-0 items-start gap-3" data-id="element-9601" data-mp="1ioemvw7gzn9i">
                  <IconTile name="accommodation" tone="brand" data-id="element-9602" data-mp="1imk1kgsadt4w" />
                  <div className="min-w-0" data-id="element-9603" data-mp="1drekbv9tx2n5">
                    <h2 id={`power-${property.id}`} className="text-h4 font-semibold text-ink" data-id="element-9604" data-mp="18qqs569v6frc">
                      {property.name}
                    </h2>
                    <p className="mt-0.5 truncate text-body-sm text-ink-muted" data-id="element-9605" data-mp="toidr018iead">
                      {property.address}
                    </p>
                  </div>
                </div>
                <PowerStatusPill state={status.state} data-id="element-9606" data-mp="13ldb3flru8n2" />
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1" data-id="element-9607" data-mp="13k23f4p7ggqo">
                <FreshnessLine at={status.lastConfirmedAt} data-id="element-9608" data-mp="18iwz3789tzs7" />
                <ConfidenceLine confidence={status.confidence} data-id="element-9609" data-mp="ayacyol0bzxc" />
              </div>

              {status.state === 'unknown' ? <p className="mt-3 rounded-lg border border-line bg-canvas px-3 py-2 text-body-sm text-ink-muted" data-id="element-9610" data-mp="fwlrhcm8yylw">
                  No recent power reports for this {one}. Your confirmation would be the
                  first thing residents see.
                </p> : <dl className="mt-3 space-y-1 text-caption text-ink-muted" data-id="element-9611" data-mp="3obm3yy907yoi">
                  <div className="flex justify-between gap-3" data-id="element-9612" data-mp="3ta6ych1kro51">
                    <dt data-id="element-9613" data-mp="3la9ajkiogy0h">Reports counting now</dt>
                    <dd className="cz-num text-ink" data-id="element-9614" data-mp="3l8y6djlblo8u">{status.reportCount}</dd>
                  </div>
                  <div className="flex justify-between gap-3" data-id="element-9615" data-mp="3gawnozrt3qxw">
                    <dt data-id="element-9616" data-mp="3v0vicsk5kk8m">Stated source</dt>
                    <dd className="text-ink" data-id="element-9617" data-mp="3uzkg4x6cvkjm">{POWER_SOURCE_LABEL[status.source]}</dd>
                  </div>
                  <div className="flex justify-between gap-3" data-id="element-9618" data-mp="3q20zjqm6qvoi">
                    <dt data-id="element-9619" data-mp="32cpwg2hd4p77">Newest evidence</dt>
                    <dd className="text-ink" data-id="element-9620" data-mp="32drckld8k1vs">
                      {status.evidence ? EVIDENCE_LABEL[status.evidence] : 'None'}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-3" data-id="element-9621" data-mp="3cbfgo1wqelqm">
                    <dt data-id="element-9622" data-mp="37ai0fuva81rr">Reliability</dt>
                    <dd className="cz-num text-ink" data-id="element-9623" data-mp="379gbqnlcfgrk">
                      {reliability.sufficient ? `${Math.round((reliability.uptimeShare ?? 0) * 100)}% powered · ${reliability.sampleDays} days` : `Not enough data (${reliability.reportCount}/${RELIABILITY_MINIMUM.reports} reports)`}
                    </dd>
                  </div>
                </dl>}

              {status.conflicting && <p className="mt-3 rounded-lg border border-warn-100 bg-warn-50 px-3 py-2 text-body-sm text-warn-800" data-id="element-9624" data-mp="l45370leezou">
                  Recent reports here disagree. Confirming what you can see yourself will
                  settle it faster than waiting.
                </p>}

              {location.personal && <p className="mt-3 text-caption text-ink-subtle" data-id="element-9625" data-mp="1ht4948yifpdv">
                  This residence is a private location: its status is shown to its own
                  residents and never on the public campus map.
                </p>}

              <div className="mt-4 flex flex-wrap gap-2.5" data-id="element-9626" data-mp="1vsvly6daizqa">
                <button type="button" onClick={() => setEditing(location.id)} className="min-h-touch rounded-xl bg-brand-700 px-4 text-body-sm font-semibold text-white transition-colors duration-150 ease-out hover:bg-brand-800" data-id="element-9627" data-mp="1vrarshuw1isy">
                  Update status
                </button>
              </div>

              {attached.length > 0 && <details className="mt-4" data-id="element-9628" data-mp="x4lbhy75adf8">
                  <summary className="min-h-touch cursor-pointer text-body-sm font-medium text-brand-700" data-id="element-9629" data-mp="x5mpaafjpe0n">
                    Reports attached to this {one} ({attached.length})
                  </summary>
                  <ul className="mt-2 divide-y divide-line" data-id="element-9630" data-mp="1puk3a1cq4onl">
                    {attached.slice(0, 6).map(item => {
                const Icon = powerIcons[SOURCE_ICON[item.source]];
                return <li key={item.id} className="flex items-center gap-3 py-2" data-id="element-9631" data-mp="1fx5m1ziz7vu8">
                          <Icon className="h-4 w-4 shrink-0 text-ink-subtle" aria-hidden data-id="element-9632" data-mp="9gafb8ce91xa" />
                          <span className="min-w-0 flex-1" data-id="element-9633" data-mp="4ecp1u5kt6uu">
                            <span className="block text-body-sm text-ink" data-id="element-9634" data-mp="2g3rj669dq8e6">
                              {POWER_STATE_LABEL[item.state]} ·{' '}
                              {POWER_SOURCE_LABEL[item.source]}
                            </span>
                            <span className="block text-caption text-ink-subtle" data-id="element-9635" data-mp="2pzui738xpqww">
                              {EVIDENCE_LABEL[item.evidence]}
                            </span>
                          </span>
                          <FreshnessLine at={item.reportedAt} data-id="element-9636" data-mp="2ztbf4g4dbm4s" />
                        </li>;
              })}
                  </ul>
                </details>}
            </section>;
      })}
      </div>

      <FeatureGate featureKey="power_meter" data-id="element-9637" data-mp="3dr9r5ev7v6h4" />

      {editing && <UpdateStatusDialog locationId={editing} offline={offline} onClose={() => setEditing(null)} onSubmit={(state, source) => submitOperatorReport({
      locationId: editing,
      state,
      source,
      evidence: 'property_provider',
      reporterLabel: 'Property manager'
    })} data-id="element-9638" data-mp="1edvwvkuqc42u" />}
    </div>;
}
function UpdateStatusDialog({
  locationId,
  offline,
  onClose,
  onSubmit
}: {
  locationId: string;
  offline: boolean;
  onClose: () => void;
  onSubmit: (state: PowerState, source: PowerSourceKind) => {
    ok: true;
  } | {
    ok: false;
    reason: string;
  };
}) {
  const [state, setState] = useState<PowerState | null>(null);
  const [source, setSource] = useState<PowerSourceKind>('grid');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const location = powerLocationById(locationId);
  return <Modal open onClose={onClose} title={done ? 'Status updated' : `Update power at ${location?.name ?? 'this property'}`} description={done ? undefined : 'Report only what you can confirm right now. This is filed as a property manager confirmation.'} data-id="element-9639" data-mp="2rd0tve3wfbeo">
      {done ? <div className="space-y-3" data-id="element-9640" data-mp="jp6hg6nny6zb">
          <Badge tone="ok" data-id="element-9641" data-mp="jo4tx2pgns0p">Recorded</Badge>
          <p className="text-body-sm text-ink" data-id="element-9642" data-mp="fnkvzwx2ckdi">
            Residents looking at this property now see your confirmation, marked as coming
            from the property manager.
          </p>
          <button type="button" onClick={onClose} className="min-h-touch w-full rounded-xl bg-brand-700 px-4 text-body-sm font-semibold text-white hover:bg-brand-800" data-id="element-9643" data-mp="17f0r6fcorffc">
            Done
          </button>
        </div> : <div className="space-y-4" data-id="element-9644" data-mp="ykm2xna6guk3">
          <fieldset data-id="element-9645" data-mp="to5gcximmo94">
            <legend className="text-body-sm font-medium text-ink" data-id="element-9646" data-mp="tmko5ejsbhef">
              What is the power situation?
            </legend>
            <div className="mt-2 grid gap-2 sm:grid-cols-2" data-id="element-9647" data-mp="1r4cbt1r79vwv">
              {STATES.map(option => <button key={option} type="button" aria-pressed={state === option} onClick={() => setState(option)} className={`min-h-touch rounded-xl border px-3 py-2 text-left text-body-sm font-medium transition-colors duration-150 ease-out ${state === option ? 'border-brand-700 bg-brand-50 text-ink' : 'border-line text-ink hover:border-line-strong'}`} data-id="element-9648" data-mp="1ma81q9mn17rk">
                  {POWER_STATE_LABEL[option]}
                </button>)}
            </div>
          </fieldset>

          <label className="block" data-id="element-9649" data-mp="1diovu9xqghk8">
            <span className="text-body-sm font-medium text-ink" data-id="element-9650" data-mp="1djqbkrpp1s8a">What is powering it?</span>
            <select value={source} onChange={event => setSource(event.target.value as PowerSourceKind)} className="mt-1 min-h-touch w-full rounded-lg border border-line bg-surface px-3 text-body text-ink focus:border-brand-700 focus:outline-none" data-id="element-9651" data-mp="1hl1t09yez75e">
              {SOURCES.map(option => <option key={option} value={option} data-id="element-9652" data-mp="w8uiseup4o5e">
                  {POWER_SOURCE_LABEL[option]}
                </option>)}
            </select>
          </label>

          {error && <p role="alert" className="rounded-lg border border-warn-100 bg-warn-50 px-3 py-2 text-body-sm text-warn-800" data-id="element-9653" data-mp="157om0o2j8crx">
              {error}
            </p>}
          {offline && <p className="rounded-lg border border-warn-100 bg-warn-50 px-3 py-2 text-caption text-warn-800" data-id="element-9654" data-mp="1f0nam5y143bj">
              You appear to be offline. The update cannot be sent until the connection
              returns.
            </p>}

          <div className="flex gap-2.5" data-id="element-9655" data-mp="3uovo6mcc60n4">
            <button type="button" onClick={onClose} className="min-h-touch flex-1 rounded-xl border border-line bg-canvas px-4 text-body-sm font-medium text-ink hover:border-line-strong" data-id="element-9656" data-mp="3upxdnvu3mpod">
              Cancel
            </button>
            <button type="button" disabled={!state} onClick={() => {
          if (!state) return;
          const outcome = onSubmit(state, source);
          if (outcome.ok) setDone(true);else setError(outcome.reason);
        }} className="min-h-touch flex-1 rounded-xl bg-brand-700 px-4 text-body-sm font-semibold text-white hover:bg-brand-800 disabled:cursor-not-allowed disabled:bg-canvas disabled:text-ink-muted" data-id="element-9657" data-mp="5bei22duct">
              Confirm
            </button>
          </div>
        </div>}
    </Modal>;
}